// ─────────────────────────────────────────────────────────────────────────────
// Paylaşılan Bölüm Editörleri
// Hem Kontrol Paneli (Ana Sayfa sekmesi) hem de Görsel Editör tarafından kullanılır.
// Her editör siteData'dan okur, siteData'ya kaydeder; kaydedince onSaved() çağırır.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import {
  siteData,
  type HeroData, type FeatureItem, type CTAData, type TestimonialItem,
  type SolutionsData, type StoriesData, type HpBannerData, type OfficesData,
  type Product,
} from "@/store/siteData";
import { productCategories, CATEGORY_IMG } from "@/mocks/products";
import { uploadFile } from "@/utils/apiClient";

// ── Ortak küçük bileşenler ───────────────────────────────────────────────────

const inputCls =
  "w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-4 py-2.5 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#5E7398]";

export function Field({ label, value, onChange, multiline = false, placeholder }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean; placeholder?: string }) {
  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      {multiline
        ? <textarea value={value} onChange={e => onChange(e.target.value)} rows={3} placeholder={placeholder} className={inputCls + " resize-none"} />
        : <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={inputCls} />}
    </div>
  );
}

export function IconField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">İkon Kodu (Remix Icon)</label>
      <div className="flex items-center gap-2">
        <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-lg bg-[#3B82F6]/15 border border-[#2B3654]">
          <i className={`${value} text-[#3B82F6] text-lg`}></i>
        </span>
        <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder="ri-server-line" className={inputCls} />
      </div>
    </div>
  );
}

export function ImageField({ label, url, onChange }: { label: string; url: string; onChange: (url: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const handle = async (file: File) => {
    setBusy(true); setErr("");
    try { const res = await uploadFile(file); onChange(res.url); }
    catch (e) { setErr(e instanceof Error ? e.message : "Yükleme hatası"); }
    setBusy(false);
  };
  return (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      <div className="flex items-start gap-3">
        <div className="w-24 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-[#2B3654] bg-[#0F1729] grid place-items-center">
          {url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <i className="ri-image-line text-[#5E7398] text-xl"></i>}
        </div>
        <div className="flex-1 space-y-2">
          <label className="flex items-center justify-center gap-2 cursor-pointer border border-dashed border-[#2B3654] hover:border-[#3B82F6] rounded-lg px-3 py-2 transition-colors">
            <i className={`text-[#3B82F6] ${busy ? "ri-loader-4-line animate-spin" : "ri-upload-cloud-line"}`}></i>
            <span className="text-[#7B8FAF] text-xs">{busy ? "Yükleniyor..." : "Görsel Yükle"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={e => e.target.files?.[0] && handle(e.target.files[0])} />
          </label>
          <input type="text" value={url} onChange={e => onChange(e.target.value)} placeholder="veya görsel URL'si yapıştırın" className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-xs rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6] placeholder-[#5E7398]" />
        </div>
      </div>
      {err && <p className="text-red-400 text-xs mt-1">{err}</p>}
    </div>
  );
}

export function SaveBar({ onSave, saved }: { onSave: () => void; saved: boolean }) {
  return (
    <div className="flex justify-end pt-4 border-t border-[#2B3654] sticky bottom-0 bg-[#141E35]/95 backdrop-blur -mx-1 px-1 pb-1">
      <button onClick={onSave} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 ${saved ? "bg-emerald-500 text-white" : "bg-[#3B82F6] hover:bg-[#2563EB] text-white"}`}>
        <i className={saved ? "ri-check-line" : "ri-save-line"}></i>
        {saved ? "Kaydedildi!" : "Kaydet"}
      </button>
    </div>
  );
}

const cardCls = "bg-[#0F1729] border border-[#2B3654] rounded-xl p-4 space-y-3";
const addBtnCls = "text-xs text-[#B8C5D8] border border-[#2B3654] px-3 py-1.5 rounded hover:border-[#3B82F6] cursor-pointer whitespace-nowrap flex items-center gap-1";
const delBtnCls = "w-7 h-7 flex items-center justify-center text-red-400 hover:text-red-300 cursor-pointer flex-shrink-0 rounded hover:bg-red-500/10";

function useEditor<T>(get: () => T, save: (d: T) => Promise<unknown>, onSaved?: () => void) {
  const [data, setData] = useState<T>(get);
  const [saved, setSaved] = useState(false);
  const handleSave = async () => {
    try {
      await save(data);
      setSaved(true);
      onSaved?.();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert("Kaydedilemedi: " + (e instanceof Error ? e.message : "bilinmeyen hata"));
    }
  };
  return { data, setData, saved, handleSave };
}

type EditorProps = { onSaved?: () => void };

// ── Hero ─────────────────────────────────────────────────────────────────────
export function HeroEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<HeroData>(siteData.getHero, siteData.saveHero, onSaved);
  const upd = (k: keyof HeroData, v: string) => setData(p => ({ ...p, [k]: v }));
  return (
    <div className="space-y-4">
      <Field label="Etiket (küçük üst yazı)" value={data.tagline} onChange={v => upd("tagline", v)} />
      <Field label="Ana Başlık" value={data.title} onChange={v => upd("title", v)} multiline />
      <Field label="Alt Metin" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <div className="grid grid-cols-2 gap-3">
        <Field label="1. Buton (Çözümler)" value={data.cta1Text} onChange={v => upd("cta1Text", v)} />
        <Field label="2. Buton (Teklif)" value={data.cta2Text} onChange={v => upd("cta2Text", v)} />
      </div>
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── Neden C2TEK (Features) ────────────────────────────────────────────────────
export function FeaturesEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<FeatureItem[]>(siteData.getFeatures, siteData.saveFeatures, onSaved);
  const upd = (id: number, k: keyof FeatureItem, v: string) => setData(p => p.map(f => f.id === id ? { ...f, [k]: v } : f));
  return (
    <div className="space-y-4">
      {data.map((f) => (
        <div key={f.id} className={cardCls}>
          <div className="flex items-center gap-3">
            <IconField value={f.icon} onChange={v => upd(f.id, "icon", v)} />
          </div>
          <Field label="Başlık" value={f.title} onChange={v => upd(f.id, "title", v)} />
          <Field label="Açıklama" value={f.desc} onChange={v => upd(f.id, "desc", v)} multiline />
          <ImageField label="Görsel" url={f.imageUrl} onChange={v => setData(p => p.map(x => x.id === f.id ? { ...x, imageUrl: v, imageId: undefined } : x))} />
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── Çözümlerimiz (Solutions) ──────────────────────────────────────────────────
export function SolutionsEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<SolutionsData>(siteData.getSolutions, siteData.saveSolutions, onSaved);
  const upd = (k: keyof SolutionsData, v: string) => setData(p => ({ ...p, [k]: v }));
  const updItem = (id: number, k: "icon" | "title" | "desc", v: string) => setData(p => ({ ...p, items: p.items.map(i => i.id === id ? { ...i, [k]: v } : i) }));
  const add = () => setData(p => ({ ...p, items: [...p.items, { id: Math.max(0, ...p.items.map(i => i.id)) + 1, icon: "ri-apps-2-line", title: "Yeni Çözüm", desc: "Açıklama" }] }));
  const remove = (id: number) => setData(p => ({ ...p, items: p.items.filter(i => i.id !== id) }));
  return (
    <div className="space-y-4">
      <Field label="Etiket" value={data.tagline} onChange={v => upd("tagline", v)} />
      <Field label="Başlık" value={data.title} onChange={v => upd("title", v)} />
      <Field label="Alt Metin" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <div className="flex items-center justify-between pt-2">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Çözüm Kartları ({data.items.length})</p>
        <button onClick={add} className={addBtnCls}><i className="ri-add-line"></i> Ekle</button>
      </div>
      {data.items.map((i) => (
        <div key={i.id} className={cardCls}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-white text-sm font-medium truncate">{i.title}</span>
            <button onClick={() => remove(i.id)} className={delBtnCls}><i className="ri-delete-bin-line text-sm"></i></button>
          </div>
          <IconField value={i.icon} onChange={v => updItem(i.id, "icon", v)} />
          <Field label="Başlık" value={i.title} onChange={v => updItem(i.id, "title", v)} />
          <Field label="Açıklama" value={i.desc} onChange={v => updItem(i.id, "desc", v)} multiline />
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── Referanslar (Testimonials) ────────────────────────────────────────────────
export function TestimonialsEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<TestimonialItem[]>(siteData.getTestimonials, siteData.saveTestimonials, onSaved);
  const upd = (id: number, k: keyof TestimonialItem, v: string) => setData(p => p.map(t => t.id === id ? { ...t, [k]: v } : t));
  const add = () => setData(p => [...p, { id: Math.max(0, ...p.map(t => t.id)) + 1, quote: "Yeni referans", name: "Ad Soyad", title: "Unvan", company: "Şirket", imageUrl: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=400&q=80" }]);
  const remove = (id: number) => setData(p => p.filter(t => t.id !== id));
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Referanslar ({data.length})</p>
        <button onClick={add} className={addBtnCls}><i className="ri-add-line"></i> Ekle</button>
      </div>
      {data.map((t) => (
        <div key={t.id} className={cardCls}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-white text-sm font-medium truncate">{t.name}</span>
            <button onClick={() => remove(t.id)} className={delBtnCls}><i className="ri-delete-bin-line text-sm"></i></button>
          </div>
          <Field label="Yorum" value={t.quote} onChange={v => upd(t.id, "quote", v)} multiline />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Ad Soyad" value={t.name} onChange={v => upd(t.id, "name", v)} />
            <Field label="Unvan" value={t.title} onChange={v => upd(t.id, "title", v)} />
            <Field label="Şirket" value={t.company} onChange={v => upd(t.id, "company", v)} />
          </div>
          <ImageField label="Fotoğraf" url={t.imageUrl} onChange={v => setData(p => p.map(x => x.id === t.id ? { ...x, imageUrl: v, imageId: undefined } : x))} />
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── CTA ───────────────────────────────────────────────────────────────────────
export function CTAEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<CTAData>(siteData.getCTA, siteData.saveCTA, onSaved);
  const upd = (k: keyof CTAData, v: string) => setData(p => ({ ...p, [k]: v }));
  const updTrust = (i: number, k: "icon" | "text", v: string) => setData(p => ({ ...p, trustItems: p.trustItems.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const add = () => setData(p => ({ ...p, trustItems: [...p.trustItems, { icon: "ri-star-line", text: "Yeni" }] }));
  const remove = (i: number) => setData(p => ({ ...p, trustItems: p.trustItems.filter((_, idx) => idx !== i) }));
  return (
    <div className="space-y-4">
      <Field label="Etiket" value={data.tag} onChange={v => upd("tag", v)} />
      <Field label="Başlık" value={data.title} onChange={v => upd("title", v)} multiline />
      <Field label="Alt Açıklama" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <div className="grid grid-cols-2 gap-3">
        <Field label="1. Buton" value={data.cta1Text} onChange={v => upd("cta1Text", v)} />
        <Field label="2. Buton" value={data.cta2Text} onChange={v => upd("cta2Text", v)} />
      </div>
      <div className="flex items-center justify-between pt-2">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Güven Göstergeleri</p>
        <button onClick={add} className={addBtnCls}><i className="ri-add-line"></i> Ekle</button>
      </div>
      {data.trustItems.map((t, i) => (
        <div key={i} className="flex items-center gap-2 bg-[#0F1729] p-2.5 rounded-lg border border-[#2B3654]">
          <input value={t.icon} onChange={e => updTrust(i, "icon", e.target.value)} className="w-32 bg-[#141E35] border border-[#2B3654] text-white text-xs rounded px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
          <i className={`${t.icon} text-[#3B82F6]`}></i>
          <input value={t.text} onChange={e => updTrust(i, "text", e.target.value)} className="flex-1 bg-[#141E35] border border-[#2B3654] text-white text-sm rounded px-2 py-1.5 outline-none focus:border-[#3B82F6]" />
          <button onClick={() => remove(i)} className={delBtnCls}><i className="ri-delete-bin-line text-sm"></i></button>
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── Makaleler (Stories) ───────────────────────────────────────────────────────
export function StoriesEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<StoriesData>(siteData.getStories, siteData.saveStories, onSaved);
  const upd = (k: keyof StoriesData, v: string) => setData(p => ({ ...p, [k]: v }));
  const updItem = (id: number, k: "date" | "title" | "desc" | "imageUrl" | "body", v: string) => setData(p => ({ ...p, items: p.items.map(i => i.id === id ? { ...i, [k]: v } : i) }));
  const add = () => setData(p => ({ ...p, items: [...p.items, { id: Math.max(0, ...p.items.map(i => i.id)) + 1, imageUrl: "https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?auto=format&fit=crop&w=900&q=80", date: "Bugün", title: "Yeni Makale", desc: "Açıklama" }] }));
  const remove = (id: number) => setData(p => ({ ...p, items: p.items.filter(i => i.id !== id) }));
  return (
    <div className="space-y-4">
      <Field label="Etiket" value={data.tagline} onChange={v => upd("tagline", v)} />
      <Field label="Başlık" value={data.title} onChange={v => upd("title", v)} />
      <Field label="Alt Metin" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <div className="flex items-center justify-between pt-2">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Makaleler ({data.items.length})</p>
        <button onClick={add} className={addBtnCls}><i className="ri-add-line"></i> Ekle</button>
      </div>
      {data.items.map((i) => (
        <div key={i.id} className={cardCls}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-white text-sm font-medium truncate">{i.title}</span>
            <button onClick={() => remove(i.id)} className={delBtnCls}><i className="ri-delete-bin-line text-sm"></i></button>
          </div>
          <Field label="Tarih" value={i.date} onChange={v => updItem(i.id, "date", v)} />
          <Field label="Başlık" value={i.title} onChange={v => updItem(i.id, "title", v)} />
          <Field label="Kısa Açıklama (kartta görünür)" value={i.desc} onChange={v => updItem(i.id, "desc", v)} multiline />
          <Field label="Makale İçeriği (tam metin — paragraflar boş satırla ayrılır)" value={i.body || ""} onChange={v => updItem(i.id, "body", v)} multiline />
          <ImageField label="Görsel" url={i.imageUrl} onChange={v => updItem(i.id, "imageUrl", v)} />
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── HP Servis Bandı ───────────────────────────────────────────────────────────
export function HpBannerEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<HpBannerData>(siteData.getHpBanner, siteData.saveHpBanner, onSaved);
  const upd = (k: keyof HpBannerData, v: string) => setData(p => ({ ...p, [k]: v }));
  const updF = (id: number, k: "icon" | "title" | "sub", v: string) => setData(p => ({ ...p, features: p.features.map(f => f.id === id ? { ...f, [k]: v } : f) }));
  const add = () => setData(p => ({ ...p, features: [...p.features, { id: Math.max(0, ...p.features.map(f => f.id)) + 1, icon: "ri-check-line", title: "Yeni", sub: "" }] }));
  const remove = (id: number) => setData(p => ({ ...p, features: p.features.filter(f => f.id !== id) }));
  return (
    <div className="space-y-4">
      <Field label="Başlık" value={data.title} onChange={v => upd("title", v)} />
      <Field label="Alt Metin" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <Field label="Rozet Metni" value={data.badgeText} onChange={v => upd("badgeText", v)} />
      <ImageField label="Sağ Görsel" url={data.imageUrl} onChange={v => setData(p => ({ ...p, imageUrl: v, imageId: undefined }))} />
      <div className="flex items-center justify-between pt-2">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Özellikler ({data.features.length})</p>
        <button onClick={add} className={addBtnCls}><i className="ri-add-line"></i> Ekle</button>
      </div>
      {data.features.map((f) => (
        <div key={f.id} className={cardCls}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-white text-sm font-medium truncate">{f.title}</span>
            <button onClick={() => remove(f.id)} className={delBtnCls}><i className="ri-delete-bin-line text-sm"></i></button>
          </div>
          <IconField value={f.icon} onChange={v => updF(f.id, "icon", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Başlık" value={f.title} onChange={v => updF(f.id, "title", v)} />
            <Field label="Alt Yazı" value={f.sub} onChange={v => updF(f.id, "sub", v)} />
          </div>
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── Ofisler ───────────────────────────────────────────────────────────────────
export function OfficesEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<OfficesData>(siteData.getOffices, siteData.saveOffices, onSaved);
  const upd = (k: keyof OfficesData, v: string) => setData(p => ({ ...p, [k]: v }));
  const updCity = (cid: number, k: "label" | "code" | "searchQuery", v: string) => setData(p => ({ ...p, cities: p.cities.map(c => c.id === cid ? { ...c, [k]: v } : c) }));
  const updBranch = (cid: number, bid: number, k: "name" | "address" | "floorDoor" | "district" | "phone", v: string) =>
    setData(p => ({ ...p, cities: p.cities.map(c => c.id === cid ? { ...c, branches: c.branches.map(b => b.id === bid ? { ...b, [k]: v } : b) } : c) }));
  const addCity = () => setData(p => ({ ...p, cities: [...p.cities, { id: Math.max(0, ...p.cities.map(c => c.id)) + 1, code: "TR" + (p.cities.length + 1), label: "Yeni Şehir", searchQuery: "", branches: [] }] }));
  const removeCity = (cid: number) => setData(p => ({ ...p, cities: p.cities.filter(c => c.id !== cid) }));
  const addBranch = (cid: number) => setData(p => ({ ...p, cities: p.cities.map(c => c.id === cid ? { ...c, branches: [...c.branches, { id: Math.max(0, ...c.branches.map(b => b.id)) + 1, imageUrl: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=400&q=80", name: "Yeni Ofis", address: "", district: "", phone: "" }] } : c) }));
  const removeBranch = (cid: number, bid: number) => setData(p => ({ ...p, cities: p.cities.map(c => c.id === cid ? { ...c, branches: c.branches.filter(b => b.id !== bid) } : c) }));
  return (
    <div className="space-y-4">
      <Field label="Etiket" value={data.tagline} onChange={v => upd("tagline", v)} />
      <Field label="Başlık" value={data.title} onChange={v => upd("title", v)} />
      <Field label="Alt Metin" value={data.subtitle} onChange={v => upd("subtitle", v)} multiline />
      <div className="flex items-center justify-between pt-2">
        <p className="text-[#7B8FAF] text-xs uppercase tracking-widest">Şehirler ({data.cities.length})</p>
        <button onClick={addCity} className={addBtnCls}><i className="ri-add-line"></i> Şehir</button>
      </div>
      {data.cities.map((c) => (
        <div key={c.id} className={cardCls}>
          <div className="flex items-start justify-between gap-2">
            <span className="text-white text-sm font-medium">{c.label}</span>
            <button onClick={() => removeCity(c.id)} className={delBtnCls}><i className="ri-delete-bin-line text-sm"></i></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Şehir Adı" value={c.label} onChange={v => updCity(c.id, "label", v)} />
            <Field label="Kod" value={c.code} onChange={v => updCity(c.id, "code", v)} />
          </div>
          <Field label="Harita Arama Sorgusu (Google Maps)" value={c.searchQuery} onChange={v => updCity(c.id, "searchQuery", v)} placeholder="Adres+veya+arama+terimi" />
          <div className="flex items-center justify-between pt-1">
            <p className="text-[#5E7398] text-[11px] uppercase tracking-widest">Şubeler ({c.branches.length})</p>
            <button onClick={() => addBranch(c.id)} className={addBtnCls}><i className="ri-add-line"></i> Şube</button>
          </div>
          {c.branches.map((b) => (
            <div key={b.id} className="bg-[#141E35] border border-[#2B3654] rounded-lg p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <span className="text-[#B8C5D8] text-xs font-medium">{b.name}</span>
                <button onClick={() => removeBranch(c.id, b.id)} className={delBtnCls}><i className="ri-delete-bin-line text-xs"></i></button>
              </div>
              <Field label="Ad" value={b.name} onChange={v => updBranch(c.id, b.id, "name", v)} />
              <Field label="Adres" value={b.address} onChange={v => updBranch(c.id, b.id, "address", v)} multiline />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Kat/Kapı" value={b.floorDoor || ""} onChange={v => updBranch(c.id, b.id, "floorDoor", v)} />
                <Field label="İlçe/Şehir" value={b.district} onChange={v => updBranch(c.id, b.id, "district", v)} />
              </div>
              <Field label="Telefon" value={b.phone} onChange={v => updBranch(c.id, b.id, "phone", v)} />
              <ImageField label="Görsel" url={b.imageUrl} onChange={v => setData(p => ({ ...p, cities: p.cities.map(ci => ci.id === c.id ? { ...ci, branches: ci.branches.map(br => br.id === b.id ? { ...br, imageUrl: v, imageId: undefined } : br) } : ci) }))} />
            </div>
          ))}
        </div>
      ))}
      <SaveBar onSave={handleSave} saved={saved} />
    </div>
  );
}

// ── Ürünler (Products) ────────────────────────────────────────────────────────
const catSlugs = productCategories.filter(c => c.slug !== "all") as { slug: Product["category"]; label: string }[];

export function ProductsEditor({ onSaved }: EditorProps) {
  const { data, setData, saved, handleSave } = useEditor<Product[]>(siteData.getProducts, siteData.saveProducts, onSaved);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<Product["category"] | "all">("all");
  const [editId, setEditId] = useState<number | null>(null);

  const upd = (id: number, patch: Partial<Product>) => setData(p => p.map(x => x.id === id ? { ...x, ...patch } : x));
  const add = () => {
    const id = Math.max(0, ...data.map(p => p.id)) + 1;
    setData(p => [{ id, code: "YENI-KOD", dmo: "", desc: "", type: "Taşınabilir Bilgisayar", category: "laptop", brand: "HP", featured: false }, ...p]);
    setEditId(id);
  };
  const remove = (id: number) => { if (!confirm("Bu ürün silinsin mi?")) return; setData(p => p.filter(x => x.id !== id)); if (editId === id) setEditId(null); };

  const filtered = data.filter(p =>
    (cat === "all" || p.category === cat) &&
    (q.trim() === "" || `${p.code} ${p.desc} ${p.type} ${p.dmo}`.toLowerCase().includes(q.toLowerCase()))
  );
  const editing = data.find(p => p.id === editId) || null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-[#5E7398]"></i>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Ürün ara (kod, açıklama)..." className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg pl-9 pr-3 py-2.5 outline-none focus:border-[#3B82F6] placeholder-[#5E7398]" />
        </div>
        <button onClick={add} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white text-sm font-medium px-4 py-2.5 rounded-lg cursor-pointer flex items-center gap-1.5 whitespace-nowrap"><i className="ri-add-line"></i> Ürün Ekle</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {[{ slug: "all", label: "Tümü" }, ...catSlugs].map(c => (
          <button key={c.slug} onClick={() => setCat(c.slug as Product["category"] | "all")} className={`text-xs px-3 py-1.5 rounded-lg border cursor-pointer ${cat === c.slug ? "bg-[#3B82F6]/20 text-white border-[#3B82F6]/50" : "text-[#7B8FAF] border-[#2B3654] hover:text-white"}`}>{c.label}</button>
        ))}
      </div>
      <p className="text-[#5E7398] text-xs">{filtered.length} / {data.length} ürün</p>

      {/* Kart ızgarası — kapsayıcı genişliğine göre uyum sağlar */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3">
        {filtered.map((p) => (
          <div key={p.id} className="group bg-[#0F1729] border border-[#2B3654] rounded-xl overflow-hidden flex flex-col hover:border-[#3B82F6]/50 transition-colors">
            <div className="relative aspect-[4/3] bg-[#0B1322] overflow-hidden">
              <img src={p.image || CATEGORY_IMG[p.category] || CATEGORY_IMG.other} alt={p.type} onClick={() => setEditId(p.id)} className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              <span className="absolute top-2 left-2 bg-[#0F172A]/90 text-white text-[10px] font-bold px-1.5 py-0.5 rounded pointer-events-none">{p.brand}</span>
              <button onClick={() => upd(p.id, { featured: !p.featured })} title="Öne çıkar" className={`absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full bg-[#0A1020]/80 cursor-pointer ${p.featured ? "text-amber-400" : "text-[#7B8FAF] hover:text-amber-400"}`}>
                <i className={p.featured ? "ri-star-fill" : "ri-star-line"}></i>
              </button>
            </div>
            <div className="p-3 flex flex-col flex-1">
              <p className="text-white text-xs font-semibold leading-tight truncate" title={p.code}>{p.code}</p>
              <p className="text-[#5E7398] text-[11px] truncate mt-0.5">{p.type}</p>
              <div className="flex items-center gap-1.5 mt-3">
                <button onClick={() => setEditId(p.id)} className="flex-1 text-xs text-[#B8C5D8] border border-[#2B3654] rounded-lg py-1.5 hover:border-[#3B82F6] hover:text-white cursor-pointer flex items-center justify-center gap-1"><i className="ri-edit-line"></i> Düzenle</button>
                <button onClick={() => remove(p.id)} className="w-8 h-8 grid place-items-center text-red-400 hover:text-red-300 border border-[#2B3654] hover:border-red-500/40 rounded-lg cursor-pointer flex-shrink-0"><i className="ri-delete-bin-line text-sm"></i></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <SaveBar onSave={handleSave} saved={saved} />

      {/* Düzenleme modalı */}
      {editing && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" onClick={() => setEditId(null)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
          <div className="relative z-10 w-full max-w-lg max-h-[88vh] overflow-y-auto rounded-2xl bg-[#141E35] border border-[#2B3654] shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 flex items-center justify-between gap-2 px-5 py-3 border-b border-[#2B3654] bg-[#141E35]">
              <span className="text-white text-sm font-semibold truncate">Ürün Düzenle — {editing.code}</span>
              <button onClick={() => setEditId(null)} className="w-8 h-8 grid place-items-center text-[#7B8FAF] hover:text-white cursor-pointer rounded-lg hover:bg-white/5"><i className="ri-close-line text-xl"></i></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Ürün Kodu" value={editing.code} onChange={v => upd(editing.id, { code: v })} />
                <Field label="DMO Kodu" value={editing.dmo} onChange={v => upd(editing.id, { dmo: v })} />
              </div>
              <Field label="Açıklama / Başlık" value={editing.desc} onChange={v => upd(editing.id, { desc: v })} multiline />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Tip" value={editing.type} onChange={v => upd(editing.id, { type: v })} />
                <Field label="Marka" value={editing.brand} onChange={v => upd(editing.id, { brand: v })} />
              </div>
              <div>
                <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Kategori</label>
                <select value={editing.category} onChange={e => upd(editing.id, { category: e.target.value as Product["category"] })} className={inputCls}>
                  {catSlugs.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                  <option value="other">Diğer</option>
                </select>
              </div>
              <ImageField label="Ürün Görseli (boşsa kategori görseli)" url={editing.image || ""} onChange={v => upd(editing.id, { image: v, imageId: undefined })} />
              <label className="flex items-center gap-2 text-[#B8C5D8] text-sm cursor-pointer">
                <input type="checkbox" checked={editing.featured} onChange={e => upd(editing.id, { featured: e.target.checked })} className="accent-[#3B82F6]" />
                Ana sayfada öne çıkar
              </label>
            </div>
            <div className="sticky bottom-0 flex justify-end gap-2 px-5 py-3 border-t border-[#2B3654] bg-[#141E35]">
              <button onClick={() => setEditId(null)} className="px-4 py-2 text-sm text-[#B8C5D8] border border-[#2B3654] rounded-lg hover:text-white cursor-pointer">Kapat</button>
              <button onClick={async () => { await handleSave(); setEditId(null); }} className="px-5 py-2 text-sm font-medium rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white cursor-pointer flex items-center gap-2"><i className="ri-save-line"></i> Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
